/**
 * feat-004 story 2.4 — display-options CRUD client (Planigo /service/product/:id/displayOptions*,
 * stories 2.1/2.3). Deliberately host-agnostic (AD-11): every function takes an OPTIONAL,
 * injectable `submit` function as its last parameter, defaulting to the legacy `submitSets`.
 *
 * Why injectable: `submitSets` (components/UiComponents/SubmitSets.js) pulls the JWT/store/user
 * headers from the app's GLOBAL root Redux store (`import { store } from '../../store/store'`),
 * so it only works when that store is mounted. DisplayOptionsCard must also work in hosts that
 * don't have it (AD-11 "storeless" requirement) — the injection seam is what makes that possible;
 * real hosts today (the masterdata Products screen, which DOES have the global store) simply omit
 * the parameter and get the default, identical to every other v3 service in this module.
 *
 * `submitCollection`'s display-option entries use ":id"/":uuid" TEMPLATE tokens in their URL
 * (submit.service.js) — unlike every other entry in that registry, these are REST path params,
 * not a query string or body field. `submitSets` has no path-param substitution of its own
 * (its URL-building only appends `cobj` when `queryparam: true`, and for non-GET methods `cobj`
 * is ALSO the axios request body — so a single `cobj` cannot serve as both a URL suffix AND a
 * separate JSON payload). `withPathParams` below substitutes the tokens into a per-call copy of
 * the entry BEFORE calling submit, so the payload argument stays a clean, separate JSON body.
 *
 * LAZY require (not a module-top-level require): `submit.service.js` transitively imports
 * `_translations/i18n.js` -> the full translations file -> masterdata `clusters.js` ->
 * `mdsidebarmenu.js` — a heavy, unrelated legacy chain. This module is now reachable from the
 * v3 module's `index.ts` barrel (DisplayOptionsCard is its 2nd sanctioned export, AD-11), and
 * ES/CJS module evaluation runs a barrel's ENTIRE file top-to-bottom on import regardless of
 * which named export a consumer actually uses — so a top-level require here would force every
 * consumer of `index.ts` (including every test that only wants `SimulationScreen`) to eagerly
 * load that legacy chain too, exactly like `store/edition-normalization.ts`'s own deferred-
 * require bridge exists to prevent for the app root store. Same pattern here, same reason.
 *
 * Note: only the request EXECUTOR (`submitSets`) is actually skippable via injection —
 * `submitCollection`'s URL templates are ALWAYS sourced from the real `submit.service.js`
 * regardless of whether a `submit` function is injected, since `getLegacyBridge()` always
 * requires it. A fully local URL-template source independent of `submit.service.js` would
 * also require `basePath` resolution to become lazy — disproportionate to this story.
 */
import type { CanonicalDisplayOption } from '../model/contract.types'
import type {
  BulkImportResult,
  DisplayModeImportRow,
  OrientationImportRow,
} from '../engines/display-option-excel-import'

function getLegacyBridge(): {
  submitCollection: Record<string, unknown>
  submitSets: SubmitFn
} {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { submitCollection } = require('../../../_services/submit.service')
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { submitSets } = require('../../../components/UiComponents/SubmitSets')
  return { submitCollection, submitSets }
}

export type Uom = 'mm' | 'cm' | 'inches' | 'meters' | 'feet' | 'none'
export type DisplayOptionKind = 'orientation' | 'display_mode'

/**
 * The CRUD/management shape (story 2.1's `DisplayOptionDto`) — UNFILTERED (includes
 * disabled/archived rows). Distinct from story 2.2's `DisplayOptionReadDto` (the filtered
 * picker projection with computed image fields) — this story does NOT use that shape.
 * Field names/types verified directly against `Planigo/src/dto/product/display-option-dto.ts`,
 * `enum/display-option-type.ts`, `enum/uom.ts` this session (same author, same session).
 */
export interface DisplayOptionDto {
  uuid: string
  type: DisplayOptionKind
  orientationKey: number | null
  name: string | null
  width: number | null
  height: number | null
  depth: number | null
  uom: Uom | null
  capacity: number | null
  isStackable: boolean
  maxStackableCount: number
  isHangable: boolean
  hangHoleX: number | null
  hangHoleY: number | null
  isEnabled: boolean
  isDefault: boolean
  archivedAt: string | null
  /**
   * story 2.6: resolved server-side and returned by `listOptions` as of
   * 2026-10-12 — the deferred backend fix this comment used to describe.
   * Until then they were front-end-only (seeded from a `commitOptionImage`
   * response, carried across refetches), so an image was visible ONLY in the
   * session that uploaded it; a reload, another tab or another user saw the
   * option as having none.
   *
   * The CRUD mutation responses (create/update/enable/…) still omit them —
   * they project the entity's own columns, and these two are resolved, not
   * stored. `DisplayOptionsCard`'s `withCarriedImageFields` therefore still
   * carries the last known values across those responses.
   */
  hasDedicatedImage?: boolean
  imageUrl?: string | null
}

/** Fields the CRUD create/update endpoints accept (a partial write shape). */
export type DisplayOptionWritePayload = Partial<
  Pick<
    DisplayOptionDto,
    | 'type'
    | 'name'
    | 'width'
    | 'height'
    | 'depth'
    | 'uom'
    | 'capacity'
    | 'isStackable'
    | 'maxStackableCount'
    | 'isHangable'
    | 'hangHoleX'
    | 'hangHoleY'
    | 'isEnabled'
  >
>

export interface UpdateOptionResult {
  option: DisplayOptionDto
  /** Only set when a dimension/capacity edit on a display mode has live placements (story 2.1 AC4). */
  liveInstanceCount?: number
}

/**
 * Story 4.7 — the CRUD DTO (flat width/height/depth/uom + flat flag
 * columns) has never needed converting to the canonical model shape
 * before this story: every prior consumer of this service (`OptionList`/
 * `OptionDetail`/`DisplayOptionsCard`) works with `DisplayOptionDto`
 * end-to-end and refetches its own list independently of the live
 * simulation model. `DefineHangingDialog`'s "Define and hang" commit is
 * the first caller that needs the UPDATED option to land back in
 * `CanonicalProduct.displayOptions` (so `effectiveFlags` sees the new
 * hole immediately, without a full-model reload) — hence this mapper.
 */
export const toCanonicalDisplayOption = (
  dto: DisplayOptionDto
): CanonicalDisplayOption => ({
  uuid: dto.uuid,
  type: dto.type,
  orientationKey: dto.orientationKey,
  name: dto.name,
  ...(dto.width != null && dto.height != null && dto.depth != null
    ? { dims: { w: dto.width, h: dto.height, d: dto.depth, uom: dto.uom ?? 'cm' } }
    : {}),
  capacity: dto.capacity,
  flags: {
    isStackable: dto.isStackable,
    maxStackableCount: dto.maxStackableCount,
    isHangable: dto.isHangable,
    hangHoleX: dto.hangHoleX,
    hangHoleY: dto.hangHoleY,
  },
  isEnabled: dto.isEnabled,
  isDefault: dto.isDefault,
  hasDedicatedImage: dto.hasDedicatedImage ?? false,
  ...(dto.imageUrl != null ? { imageUrl: dto.imageUrl } : {}),
})

export interface SetDefaultResult {
  previousDefault: DisplayOptionDto | null
  newDefault: DisplayOptionDto
}

export interface DeleteOptionResult {
  deleted: boolean
}

/** Matches submitSets' call shape closely enough for injection — see file header. */
export type SubmitFn = (
  entry: Record<string, unknown>,
  payload?: unknown,
  showAlerts?: boolean
) => Promise<
  | {
      status?: boolean
      extra?: unknown
      validation?: { code?: string; type?: string; msgParams?: unknown[] }
    }
  | false
  | undefined
>

/** Substitutes ":id"/":uuid" template tokens into a per-call copy of a submitCollection entry. */
function withPathParams(
  entry: Record<string, unknown>,
  params: Record<string, string | number>
): Record<string, unknown> {
  let url = String(entry.url)
  for (const [key, value] of Object.entries(params)) {
    url = url.replace(`:${key}`, encodeURIComponent(String(value)))
  }
  return { ...entry, url }
}

function unwrap<T>(
  response:
    | {
        status?: boolean
        extra?: unknown
        validation?: { code?: string; type?: string; msgParams?: unknown[] }
      }
    | false
    | undefined,
  failureCode: string
): T {
  if (!response || response.status !== true) {
    // The backend's CommonResponse validation object carries only a `code`/
    // `type`/`msgParams` enum shape, never a human-readable message (verified
    // against display-option-service-impl.ts's ValidationExceptionV2 calls) —
    // there is no localized-message mapping table for these codes anywhere in
    // this module yet, so surface the code itself rather than a bare internal
    // failure string, and include msgParams when present (e.g. a live-count).
    const validation = response ? response.validation : undefined
    const detail = validation?.code
      ? `${validation.code}${validation.msgParams?.length ? `: ${validation.msgParams.join(', ')}` : ''}`
      : undefined
    throw new Error(detail || `display-option-service/${failureCode}-failed`)
  }
  return response.extra as T
}

/**
 * Resolves the entry template (from the LAZILY-required submitCollection, even
 * when `submit` is injected — the URL template always comes from there) and
 * calls either the injected `submit` or the lazily-required real `submitSets`.
 */
async function call<T>(
  entryKey: string,
  params: Record<string, string | number>,
  payload: unknown,
  submit: SubmitFn | undefined,
  failureCode: string
): Promise<T> {
  const bridge = getLegacyBridge()
  const rawEntry = bridge.submitCollection[entryKey] as
    | Record<string, unknown>
    | undefined
  if (!rawEntry) {
    throw new Error(`display-option-service/${failureCode}-unknown-entry`)
  }
  const entry = withPathParams(rawEntry, params)
  const response = await (submit ?? bridge.submitSets)(entry, payload, false)
  return unwrap<T>(response, failureCode)
}

export const listOptions = async (
  productId: number,
  submit?: SubmitFn
): Promise<DisplayOptionDto[]> => {
  const result = await call<DisplayOptionDto[]>(
    'displayOptionList',
    { id: productId },
    undefined,
    submit,
    'list'
  )
  return result ?? []
}

/**
 * A product's PICKER list, in canonical model shape — for the two flows that
 * mint a `CanonicalProduct` client-side instead of receiving one from a v3
 * response: `AddProductModal` (master-data search) and `ImportedTab` (Excel
 * import). Both used to hardcode `displayOptions: []`
 * (deferred-work.md:171), which left the product with no resolvable option
 * until the next full Simulate — so a drop stamped `displayOptionId: ''`, the
 * canvas could not tell Front from a tray, the peg gate had no option to check,
 * and `effectiveFlags` reported neither stacking nor hanging.
 *
 * Applies the SAME filter the backend's v3 load applies
 * (`attachPickerOptionsToProducts` — non-archived, FR-5; disabled options stay
 * and render as dimmed rows since fix-044), so the list a just-added product
 * carries matches the one a simulated product carries.
 *
 * Complete as of 2026-10-12: the CRUD list endpoint now resolves each option's
 * dedicated image too, so a product added this way carries its real option
 * pictures immediately instead of waiting for the next Simulate. Dims, flags
 * and uuids — what geometry, capacity, stacking and hanging depend on — were
 * always complete.
 *
 * Never throws: a product whose options cannot be fetched keeps the empty list
 * it has today rather than blocking the add.
 */
export const fetchPickerOptions = async (
  productId: number,
  submit?: SubmitFn
): Promise<CanonicalDisplayOption[]> => {
  try {
    const options = await listOptions(productId, submit)
    // fix-044 (2026-10-22): disabled options are KEPT, matching the v3 load
    // (`attachPickerOptionsToProducts`, includeDisabled) — the orientation
    // list shows them dimmed with a reason; `isEnabled: false` blocks picking.
    return options
      .filter((option) => option.archivedAt == null)
      .map(toCanonicalDisplayOption)
  } catch {
    return []
  }
}

export const createDisplayMode = async (
  productId: number,
  payload: DisplayOptionWritePayload,
  submit?: SubmitFn
): Promise<DisplayOptionDto> =>
  call<DisplayOptionDto>(
    'displayOptionCreate',
    { id: productId },
    payload,
    submit,
    'create'
  )

export const updateOption = async (
  productId: number,
  uuid: string,
  payload: DisplayOptionWritePayload,
  submit?: SubmitFn
): Promise<UpdateOptionResult> =>
  call<UpdateOptionResult>(
    'displayOptionUpdate',
    { id: productId, uuid },
    payload,
    submit,
    'update'
  )

/**
 * Story 4.7 (AC2) — the "Define and hang" commit both entry points share:
 * enable hanging + set a default hole on the option via the existing
 * `updateOption` call (immediate catalog commit, never planogram state —
 * same as any other `DisplayOptionsCard` edit), then return the option's
 * canonical shape plus the product's FULL catalog with it merged in, so
 * the caller can dispatch `catalogUpdated` and have `effectiveFlags` see
 * the new hole immediately.
 *
 * Default hole coordinates: the spec only says "enabling hanging
 * auto-creates a default hole" without mandating coordinates, and (0, 0)
 * was used as "a documented placeholder" (Dev Notes) — the top-left corner
 * of the option's own bounds, which is where the product then visibly hung
 * from. fix-073: callers pass `hole` instead, seeded from the option's OWN
 * footprint by `seedOptionHangHoleCm` — the same centre-of-the-width rule
 * the Products screen and the Product Information modal apply, measured on
 * the orientation rather than on the product's base box. The (0, 0)
 * fallback is kept only for a caller that cannot resolve dims.
 */
export const defineAndHang = async (
  productId: number,
  optionUuid: string,
  catalog: CanonicalDisplayOption[],
  submit?: SubmitFn,
  hole: { x: number; y: number } = { x: 0, y: 0 }
): Promise<{ options: CanonicalDisplayOption[]; updated: CanonicalDisplayOption }> => {
  const result = await updateOption(
    productId,
    optionUuid,
    { isHangable: true, hangHoleX: hole.x, hangHoleY: hole.y },
    submit
  )
  const updated = toCanonicalDisplayOption(result.option)
  const options = catalog.map((option) =>
    option.uuid === updated.uuid ? updated : option
  )
  return { options, updated }
}

export const enableOption = async (
  productId: number,
  uuid: string,
  submit?: SubmitFn
): Promise<DisplayOptionDto> =>
  call<DisplayOptionDto>(
    'displayOptionEnable',
    { id: productId, uuid },
    {},
    submit,
    'enable'
  )

export const disableOption = async (
  productId: number,
  uuid: string,
  submit?: SubmitFn
): Promise<DisplayOptionDto> =>
  call<DisplayOptionDto>(
    'displayOptionDisable',
    { id: productId, uuid },
    {},
    submit,
    'disable'
  )

export const archiveOption = async (
  productId: number,
  uuid: string,
  submit?: SubmitFn
): Promise<DisplayOptionDto> =>
  call<DisplayOptionDto>(
    'displayOptionArchive',
    { id: productId, uuid },
    {},
    submit,
    'archive'
  )

export const setDefaultOption = async (
  productId: number,
  uuid: string,
  submit?: SubmitFn
): Promise<SetDefaultResult> =>
  call<SetDefaultResult>(
    'displayOptionSetDefault',
    { id: productId, uuid },
    {},
    submit,
    'set-default'
  )

export const deleteOption = async (
  productId: number,
  uuid: string,
  submit?: SubmitFn
): Promise<DeleteOptionResult> =>
  call<DeleteOptionResult>(
    'displayOptionDelete',
    { id: productId, uuid },
    undefined,
    submit,
    'delete'
  )

/** story 2.6 (step 1) — the generic presigned-PUT-URL primitive's response shape
 * (`submit.service.js`'s `getImagePutURL` entry, unchanged since it predates
 * this feature — verified against its other caller, `addnew.js`). */
export interface PresignedPutUrlResult {
  url: string
  id: number
}

/**
 * story 2.6 (step 1) — get a presigned S3 PUT URL for a new temp image row.
 * Reuses the existing generic `getImagePutURL` entry (NOT a display-option-
 * scoped URL — no `:id`/`:uuid` template tokens, so `withPathParams` no-ops).
 */
export const getImagePutUrl = async (
  fileName: string,
  submit?: SubmitFn
): Promise<PresignedPutUrlResult> =>
  call<PresignedPutUrlResult>(
    'getImagePutURL',
    {},
    { imageName: fileName },
    submit,
    'get-image-put-url'
  )

/** story 2.6 (step 2) — the per-option image commit endpoint's response shape.
 * Mirrors the backend's `DisplayOptionReadDto` (verified against
 * `display-option-image-service-impl.ts`'s `projectOptionWithDedicatedImage`)
 * — only the fields this card actually needs are declared here. */
export interface CommitOptionImageResult {
  uuid: string
  hasDedicatedImage: boolean
  imageUrl: string | null
}

/**
 * story 2.6 (step 2) — confirms a presigned-uploaded temp image (`imageId`,
 * from `getImagePutUrl`'s result) as `uuid`'s dedicated image. Commits
 * immediately to the catalog (never buffered into planogram state).
 */
export const commitOptionImage = async (
  productId: number,
  uuid: string,
  payload: { imageId: number },
  submit?: SubmitFn
): Promise<CommitOptionImageResult> =>
  call<CommitOptionImageResult>(
    'displayOptionImageCommit',
    { id: productId, uuid },
    payload,
    submit,
    'image-commit'
  )

/**
 * fix-017 — the option's full-size (`Original`) dedicated image url for the
 * Master Data click-to-enlarge preview, the way that screen's product image
 * preview fetches `findBiggerPictureProduct`. Never throws: null (no Original,
 * or the call failed) means "keep showing the thumbnail".
 */
export const getOptionBiggerImageUrl = async (
  productId: number,
  uuid: string,
  submit?: SubmitFn
): Promise<string | null> => {
  try {
    const result = await call<{ imageUrl: string | null } | null>(
      'displayOptionBiggerImage',
      { id: productId, uuid },
      undefined,
      submit,
      'bigger-image'
    )
    return result?.imageUrl || null
  } catch {
    return null
  }
}

/**
 * story 2.6 — the whole two-step upload as ONE call: presigned PUT URL ->
 * raw S3 PUT -> catalog commit. Lives here rather than in the component
 * because the flow is now driven by the detail pane's Save button (user
 * decision 2026-10-12 — the file is staged on drop, uploaded on save, the
 * way the Product Information tab defers its own image PUT to the product
 * save); keeping it in the service leaves `OptionImageUpload` presentational
 * and makes the flow testable without a DOM.
 *
 * `axios` is required lazily for the same barrel reason as `getLegacyBridge`
 * — it is the one dependency here that is NOT already pulled in by the
 * module's existing lazy chain, and a top-level import would load it for
 * every consumer of `index.ts`.
 */
export const uploadAndCommitOptionImage = async (
  productId: number,
  uuid: string,
  file: File,
  submit?: SubmitFn
): Promise<CommitOptionImageResult> => {
  const presigned = await getImagePutUrl(file.name, submit)
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const axios = require('axios')
  await axios({
    url: presigned.url,
    method: 'PUT',
    data: file,
    // The REAL file type, never a hardcoded 'image/*' wildcard (which is an
    // accept-pattern, not a MIME type): a content-type-scoped presigned
    // signature rejects it, and S3 stores the object mistyped.
    headers: { 'Content-Type': file.type },
  })
  return commitOptionImage(productId, uuid, { imageId: presigned.id }, submit)
}

/**
 * story 5.2 — the two new Excel bulk-import endpoints (story 5.1). No
 * ":id"/":uuid" path params (not per-product routes — barcode travels
 * per-row in the body), so `params` is always `{}`.
 */
export const bulkImportOrientations = async (
  rows: OrientationImportRow[],
  submit?: SubmitFn
): Promise<BulkImportResult> =>
  call<BulkImportResult>(
    'displayOptionBulkOrientations',
    {},
    { rows },
    submit,
    'bulk-orientations'
  )

export const bulkImportDisplayModes = async (
  rows: DisplayModeImportRow[],
  submit?: SubmitFn
): Promise<BulkImportResult> =>
  call<BulkImportResult>(
    'displayOptionBulkDisplayModes',
    {},
    { rows },
    submit,
    'bulk-display-modes'
  )
